import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AnnotationField,
  AnnotationFieldsEditor,
} from '@/components/treatment/v3/request/package-edit/MissingFieldsEditor';
import { Button } from '@/components/ui/catalyst/button';
import { countBlockingMissingFields } from '@/lib/missingFields';
import { cn } from '@/lib/utils';
import type { CorrespondenceComponentSchema } from '@/services/api/generated/correspondenceService';
import type { MatterFlattenedContext } from '@/services/api/matterFlattenedContextService';

import { useCorrespondenceUI } from '../contexts/CorrespondenceUIContext';
import { type ComponentValidationResult, usePackageComponents } from '../hooks';
import { getComponentTitle } from '../utils/component';
import type { MatterContext } from '../utils/fieldValidation';
import { RenderStatusIcon } from '../utils/status';
import {
  type ComponentCoverageSelections,
  ComponentCoverageSelector,
  hasAnyCoverageSlotRequirements,
  type RequiredEntitySlotsSchema,
  slotNameToApiField,
} from './ComponentCoverageSelector';
import { ComponentTypeLabels } from './constants';
import { CorrespondenceFieldEditor } from './CorrespondenceFieldEditor';
import type { CoverageSlotName } from './CoverageSlotSelector';

interface ComponentItemProps {
  component: CorrespondenceComponentSchema;
  /** Matter ID for fetching coverages */
  matterId: string;
  matterContext?: MatterFlattenedContext | undefined;
  validationResult?: ComponentValidationResult;
  isLoadingValidation?: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  /** Temporarily disabled during mutations (e.g., reordering) */
  disabled?: boolean;
  /** Controls whether editing is allowed. Set to 'view' when package is transmitted. */
  packageMode: 'view' | 'edit';
  /** Package-level status - when package is queued/generating, components inherit that state */
  packageStatus?: string;
}

/**
 * Component item with expandable configuration
 * Displays component status and allows inline editing
 */
export const ComponentItem: React.FC<ComponentItemProps> = ({
  component: rawComponent,
  matterId,
  matterContext = {},
  validationResult,
  isSelected,
  onSelect,
  onRemove,
  disabled = false,
  packageMode,
  packageStatus,
}) => {
  // Use component directly - API types now include coverage fields and required_entity_slots
  const component = rawComponent;

  const { packageId } = useCorrespondenceUI();
  const { updateComponentOverrides, updateComponentCoverage } =
    usePackageComponents({
      packageId,
      skip: !packageId,
    });

  const [showFields, setShowFields] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, string>>(
    component.overrides || {},
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingCoverage, setIsSavingCoverage] = useState(false);

  // Sync local overrides when component data changes
  useEffect(() => {
    setOverrides(component.overrides || {});
  }, [component.overrides]);

  // Determine if this component has coverage slot requirements
  const requiredEntitySlots = (
    component.template as Record<string, unknown> | undefined
  )?.required_entity_slots as RequiredEntitySlotsSchema | undefined;
  const hasCoverageSlots = hasAnyCoverageSlotRequirements(requiredEntitySlots);

  // Current coverage selections for this component
  const currentCoverageSelections: ComponentCoverageSelections = {
    coverage_1p_id: component.coverage_1p_id,
    coverage_3p_id: component.coverage_3p_id,
    coverage_health_id: component.coverage_health_id,
  };

  // Handle coverage slot changes
  const handleCoverageSlotChange = useCallback(
    async (slotName: CoverageSlotName, coverageId: string | null) => {
      setIsSavingCoverage(true);
      try {
        const apiField = slotNameToApiField(slotName);
        await updateComponentCoverage(component.id, {
          [apiField]: coverageId,
        });
        toast.success('Coverage updated - re-rendering', {
          description: `${slotName.toUpperCase()} coverage ${coverageId ? 'selected' : 'cleared'}`,
        });
      } catch {
        toast.error('Failed to update coverage');
      } finally {
        setIsSavingCoverage(false);
      }
    },
    [component.id, updateComponentCoverage],
  );

  const handleSetOverride = (key: string, value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveOverrides = async () => {
    setIsSaving(true);
    try {
      await updateComponentOverrides(component.id, overrides);
      toast.success('Fields saved - package re-rendering', {
        description: 'Your changes are being processed',
      });
      setShowFields(false);
    } catch (error) {
      toast.error('Failed to update fields');
    } finally {
      setIsSaving(false);
    }
  };

  const isMergeDocument = component.component_type === 'merge_document';
  const mergeDocumentSourceName =
    component.blob?.original_filename || component.blob?.name || undefined;
  const mergeDocumentTemplateName =
    component.template?.display_name || component.template?.name || undefined;

  const componentTitle = getComponentTitle(component);

  // Only inherit package's queued/generating status if this component needs re-render
  const packageIsRendering =
    packageStatus === 'queued_for_render' || packageStatus === 'generating';
  const showPackageStatus = component.needs_rerender && packageIsRendering;

  const effectiveStatus = showPackageStatus ? packageStatus : component.status;

  const statusIndicator = effectiveStatus ? (
    <RenderStatusIcon status={effectiveStatus} size="sm" />
  ) : null;

  // Count missing fields from the component's missing_fields dict
  const missingFieldsCount = countBlockingMissingFields(
    component.missing_fields,
  );

  const hasChanges =
    JSON.stringify(overrides) !== JSON.stringify(component.overrides || {});
  const canExpand =
    (component.component_type === 'template' ||
      component.component_type === 'global_template' ||
      component.component_type === 'merge_document') &&
    component.v2_template_id;

  const isEditMode = packageMode === 'edit';
  // Interactions disabled if: not in edit mode (transmitted) or busy during mutation
  const isInteractionDisabled = !isEditMode || disabled;

  // Sortable hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: component.id,
    disabled: isInteractionDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isQueued =
    component.status === 'queued_for_render' ||
    (showPackageStatus && packageStatus === 'queued_for_render');
  const isGenerating =
    component.status === 'generating' ||
    (showPackageStatus && packageStatus === 'generating');

  // Tooltip for stale indicator
  const staleTooltip = component.needs_rerender
    ? 'Inputs changed since last render'
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      title={staleTooltip}
      className={cn(
        'rounded-lg border transition-all duration-150',
        isSelected && 'border-indigo-400 bg-indigo-50/60 shadow-sm',
        !isSelected &&
          isQueued &&
          'border-violet-200 bg-violet-50/30 animate-pulse',
        !isSelected &&
          isGenerating &&
          'border-blue-200 bg-blue-50/30 animate-pulse',
        !isSelected &&
          !isQueued &&
          !isGenerating &&
          'border-zinc-200 bg-white hover:border-zinc-300',
        // Stale indicator (needs re-render) - left accent + background tint
        !isSelected &&
          !isQueued &&
          !isGenerating &&
          component.needs_rerender &&
          'border-l-2 border-l-amber-400 bg-amber-50/60',
        isDragging && 'opacity-50 shadow-lg ring-2 ring-indigo-200',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Main Component Row */}
      <div
        className={cn(
          'group relative cursor-pointer',
          showFields && 'border-b border-zinc-200',
        )}
        onClick={() => {
          if (!isInteractionDisabled) {
            onSelect();
          }
        }}
        onKeyDown={(e) => {
          if (!isInteractionDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect();
          }
        }}
        role="button"
        tabIndex={isInteractionDisabled ? -1 : 0}
      >
        <div className="flex gap-1 px-1.5 py-1">
          {/* Drag Handle - only show in edit mode */}
          {isEditMode && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className={cn(
                'flex-shrink-0 self-center p-0.5 cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 transition-colors',
                disabled && 'cursor-not-allowed opacity-50',
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <Bars3Icon className="h-3 w-3" />
            </button>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  'text-[11px] font-medium truncate leading-tight',
                  isSelected ? 'text-indigo-900' : 'text-zinc-800',
                )}
                title={componentTitle}
              >
                {componentTitle}
              </span>
              {statusIndicator}
            </div>

            {/* Meta: type + actions inline */}
            <div className="flex items-center gap-1 mt-px text-[10px] leading-tight">
              <span className="text-zinc-400 truncate">
                {component.template?.format
                  ? `${component.template.format} ${ComponentTypeLabels[component.component_type]}`
                  : ComponentTypeLabels[component.component_type]}
              </span>
              {missingFieldsCount > 0 && (
                <>
                  <span className="text-zinc-300">·</span>
                  <span className="text-amber-600 font-medium whitespace-nowrap">
                    {missingFieldsCount} missing
                  </span>
                </>
              )}
              {canExpand && isEditMode && (
                <>
                  <span className="text-zinc-300">·</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFields(!showFields);
                    }}
                    className="hover:underline font-medium text-indigo-600 whitespace-nowrap"
                  >
                    {showFields ? 'Close' : 'Edit'}
                  </button>
                </>
              )}
            </div>

            {isMergeDocument &&
              (mergeDocumentSourceName || mergeDocumentTemplateName) && (
                <div
                  className="text-[9px] text-zinc-400 mt-px leading-tight truncate"
                  title={`${mergeDocumentSourceName ? `Source: ${mergeDocumentSourceName}` : ''}${mergeDocumentSourceName && mergeDocumentTemplateName ? ' · ' : ''}${mergeDocumentTemplateName ? `Template: ${mergeDocumentTemplateName}` : ''}`}
                >
                  {mergeDocumentSourceName && (
                    <>Source: {mergeDocumentSourceName}</>
                  )}
                  {mergeDocumentSourceName &&
                    mergeDocumentTemplateName &&
                    ' · '}
                  {mergeDocumentTemplateName && (
                    <>Template: {mergeDocumentTemplateName}</>
                  )}
                </div>
              )}
          </div>

          {/* Remove Button - only show in edit mode */}
          {isEditMode && (
            <button
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (!disabled) {
                  onRemove();
                }
              }}
              className={cn(
                'flex-shrink-0 self-center text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all',
                'p-0.5 rounded hover:bg-red-50',
                disabled && 'cursor-not-allowed',
              )}
              disabled={disabled}
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Fields Section - only editable in edit mode */}
      {showFields &&
        (component.component_type === 'template' ||
          component.component_type === 'global_template' ||
          component.component_type === 'merge_document') &&
        component.v2_template_id &&
        isEditMode && (
          <div className="p-2 bg-zinc-50/80 rounded-b-lg">
            <div className="space-y-2">
              {/* Coverage Slot Selector - only for templates with coverage requirements */}
              {hasCoverageSlots && (
                <div className="pb-3 border-b border-zinc-200">
                  <ComponentCoverageSelector
                    matterId={matterId}
                    requiredSlots={requiredEntitySlots}
                    selections={currentCoverageSelections}
                    onSlotChange={handleCoverageSlotChange}
                    disabled={disabled || isSavingCoverage}
                    compact
                  />
                </div>
              )}

              {component.component_type === 'merge_document' ? (
                <AnnotationFieldsEditor
                  annotationFields={
                    (validationResult?.annotationFields ||
                      []) as AnnotationField[]
                  }
                  overrides={overrides}
                  setOverride={handleSetOverride}
                />
              ) : (
                <CorrespondenceFieldEditor
                  templateFields={validationResult?.templateFields}
                  matterContext={matterContext as MatterContext}
                  overrides={overrides}
                  savedOverrides={component.overrides || {}}
                  setOverride={handleSetOverride}
                  missingFields={validationResult?.missingFields}
                  inline
                />
              )}

              {hasChanges && (
                <div className="flex items-center justify-end gap-1.5 pt-2 mt-1 border-t border-zinc-100">
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setOverrides(component.overrides || {});
                      setShowFields(false);
                    }}
                    plain
                    className="h-6 px-2 text-[11px] font-medium text-zinc-500 hover:text-zinc-700"
                    disabled={isSaving}
                  >
                    Discard
                  </Button>
                  <Button
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleSaveOverrides();
                    }}
                    color="indigo"
                    className="h-6 px-2.5 text-[11px] font-medium shadow-sm"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};
